import { useSearchParams } from "react-router-dom";
import ManagementsAgentWrapper from "./ManagementsAgentWrapper/ManagementsAgentWrapper";
import useCallCenterManagement from "../../core/hooks/managements/callCenter/useCallCenterManagement";
import { useTranslation } from "react-i18next";
import ConsumptionsManagementsDeliveryAddressComponent from "../ConsumptionsManagementsDeliveryAddressComponent/ConsumptionsManagementsDeliveryAddressComponent";
import {
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  AGENT_MANAGEMENT_SERVICE_CONSTANTS,
  AGENT_MANAGEMENT_TYPE_QUERY_PARAM,
  AGENT_MANAGEMENT_TYPES,
} from "../../core/constants/agentManagements.constants";
import ManagementUploadFile from "../Managements/ManagementsUploadFile/ManagementsUploadFile";
import ManagementsRequestState from "../Managements/ManagementsRequestState/ManagementsRequestState";
import { useMemo } from "react";
import { isEmpty } from "lodash";

export default function ManagementsAgent() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const managementType: AGENT_MANAGEMENT_TYPES | null = useMemo(
    () =>
      searchParams.get(
        AGENT_MANAGEMENT_TYPE_QUERY_PARAM,
      ) as AGENT_MANAGEMENT_TYPES,
    [searchParams],
  );

  if (!managementType) {
    throw new Error("Management type query is required");
  }

  const managementData = useMemo(
    () => AGENT_MANAGEMENT_SERVICE_CONSTANTS[managementType],
    [managementType],
  );

  const isFileRequired = managementData.isFileRequired ?? false;

  const {
    setFile,
    file,
    step,
    managementsRequestStateProps,
    onSubmit,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    cupsOptions,
    additionalText,
    setAdditionalText,
  } = useCallCenterManagement(managementData);

  const isSubmitButtonDisabled = useMemo(
    () =>
      isFileRequired
        ? isEmpty(selectedDeliveryAddress.idContract) || !file
        : isEmpty(selectedDeliveryAddress.idContract),
    [selectedDeliveryAddress.idContract, isFileRequired, file],
  );

  const cupOptionFilteredList = useMemo(() => {
    if (managementData.cupOptionsFilter === "all") {
      return cupsOptions;
    } else {
      return cupsOptions.filter(
        (cup) =>
          cup.envirorment.toLowerCase() === managementData.cupOptionsFilter,
      );
    }
  }, [cupsOptions, managementData.cupOptionsFilter]);

  return (
    <ManagementsAgentWrapper
      onSubmit={onSubmit}
      step={step}
      title={managementData?.titleKey || ""}
      submitButtonTitle={t("Gestiones.Enviar solicitud")}
      isButtonDisabled={isSubmitButtonDisabled}
    >
      {step === 0 && (
        <>
          <Stack width={{ xs: "100%", md: "50%" }}>
            <ConsumptionsManagementsDeliveryAddressComponent
              ownerDataCups={cupOptionFilteredList}
              ownerDataCupSelected={selectedDeliveryAddress}
              setOwnerDataCupSelected={setSelectedDeliveryAddress}
            />
          </Stack>
          <FormControl
            sx={{
              display: "flex",
              gap: "1rem",
            }}
          >
            <FormLabel htmlFor="claim-detail">
              <Typography fontWeight={600}>
                {t("Gestiones.Detalle su petición")}
                <Typography variant="body2" component="span">
                  {" "}
                  - {t("Opcional")}
                </Typography>
              </Typography>
            </FormLabel>
            <TextField
              value={additionalText}
              onChange={(e) => setAdditionalText(e.target.value)}
              multiline
              rows={4}
            />
          </FormControl>

          <ManagementUploadFile
            title={t("Gestiones.Attach file")}
            isRequired={isFileRequired}
            adjunstFileButtonText={t("Gestiones.Attach file")}
            inputFileInstructions={isFileRequired}
            onFileUpload={(id, file) => {
              setFile(file);
            }}
            onFileRemove={() => {
              setFile(null);
            }}
          />
        </>
      )}

      {step > 0 && (
        <ManagementsRequestState {...managementsRequestStateProps} />
      )}
    </ManagementsAgentWrapper>
  );
}
